"use client";

import { Building2, UserRound, X } from "lucide-react";
import { useRouter } from "next/navigation";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

type RegisterTypeModalContextValue = {
  openRegisterTypeModal: () => void;
  closeRegisterTypeModal: () => void;
};

const RegisterTypeModalContext =
  createContext<RegisterTypeModalContextValue | null>(null);

type RegisterTypeModalProviderProps = {
  children: ReactNode;
};

export function RegisterTypeModalProvider({
  children,
}: RegisterTypeModalProviderProps) {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);

  const openRegisterTypeModal = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeRegisterTypeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        closeRegisterTypeModal();
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, closeRegisterTypeModal]);

  function handleSelection(
    type: "empresa" | "pessoa-fisica",
  ) {
    sessionStorage.setItem("register-type", type);
    closeRegisterTypeModal();
    router.push("/cadastro");
  }

  return (
    <RegisterTypeModalContext.Provider
      value={{
        openRegisterTypeModal,
        closeRegisterTypeModal,
      }}
    >
      {children}

      {isOpen && (
        <div
          className="fixed inset-0 z-[9999] flex min-h-screen w-screen items-center justify-center bg-slate-950/60 px-4 py-8 backdrop-blur-sm"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeRegisterTypeModal();
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="register-type-title"
            aria-describedby="register-type-description"
            className="relative w-full max-w-2xl rounded-[32px] bg-white p-6 shadow-[0_30px_100px_rgba(15,23,42,0.35)] sm:p-9"
          >
            <button
              type="button"
              onClick={closeRegisterTypeModal}
              aria-label="Fechar modal"
              className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            >
              <X size={22} />
            </button>

            <div className="text-center">
              <h2
                id="register-type-title"
                className="pr-8 text-2xl font-bold tracking-tight text-slate-950 sm:pr-0 sm:text-3xl"
              >
                Como você deseja se cadastrar?
              </h2>

              <p
                id="register-type-description"
                className="mt-3 text-sm text-slate-500 sm:text-base"
              >
                Escolha uma das opções abaixo.
              </p>
            </div>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <button
                type="button"
                onClick={() =>
                  handleSelection("pessoa-fisica")
                }
                className="group flex min-h-56 flex-col items-center justify-center rounded-3xl border border-slate-200 bg-white p-6 text-center transition-all hover:-translate-y-1 hover:border-blue-400 hover:shadow-[0_18px_45px_rgba(37,99,235,0.15)] focus:outline-none focus:ring-4 focus:ring-blue-100"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 transition group-hover:bg-blue-600 group-hover:text-white">
                  <UserRound size={31} />
                </span>

                <span className="mt-5 block text-xl font-bold text-slate-950">
                  Sou pessoa física
                </span>

                <span className="mt-3 block max-w-56 text-sm leading-6 text-slate-500">
                  Quero divulgar meu negócio utilizando meu CPF.
                </span>
              </button>

              <button
                type="button"
                onClick={() =>
                  handleSelection("empresa")
                }
                className="group flex min-h-56 flex-col items-center justify-center rounded-3xl border border-slate-200 bg-white p-6 text-center transition-all hover:-translate-y-1 hover:border-blue-400 hover:shadow-[0_18px_45px_rgba(37,99,235,0.15)] focus:outline-none focus:ring-4 focus:ring-blue-100"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 transition group-hover:bg-blue-600 group-hover:text-white">
                  <Building2 size={31} />
                </span>

                <span className="mt-5 block text-xl font-bold text-slate-950">
                  Sou empresa
                </span>

                <span className="mt-3 block max-w-56 text-sm leading-6 text-slate-500">
                  Quero divulgar minha empresa utilizando meu CNPJ.
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </RegisterTypeModalContext.Provider>
  );
}

export function useRegisterTypeModal() {
  const context = useContext(RegisterTypeModalContext);

  if (!context) {
    throw new Error(
      "useRegisterTypeModal deve ser usado dentro de RegisterTypeModalProvider.",
    );
  }

  return context;
}
